import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import * as Styles from '../styles';
import {
    ERROR_MESSAGES,
    REPORT_STATUS,
    SNACKBAR_TYPES,
    SUCCESS_MESSAGES,
} from '../config';
import { refetchReportStatus } from '../api';
import { useState, useEffect, useContext } from 'react';
import { useViewport } from 'utils/responsive';
import SnackBar from 'library/snackbar';
import {
    SegmentEventActions,
    SegmentEventActionTypes,
    SegmentEventNameTypes,
    SegmentEventPages,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';

export interface ReportCardProps {
    isLoading?: boolean;
    created_on: string;
    from_date: string;
    status: string;
    to_date: string;
    request_id?: string;
    handleDownloadReport?: any;
    sendSegmentEvent?: any;
}

const ReportCard = ({
    isLoading,
    created_on,
    status,
    from_date,
    to_date,
    request_id,
    handleDownloadReport,
    sendSegmentEvent,
}: ReportCardProps) => {
    const [currentStatus, setCurrentStatus] = useState(status);
    const [loading, setLoading] = useState(isLoading);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const { userDetails } = useContext(MPosContext);
    const { isMobile } = useViewport();

    const isComplete = currentStatus === REPORT_STATUS.COMPLETED;
    const isProcessing = currentStatus === REPORT_STATUS.RUNNING;
    const isPending = currentStatus === REPORT_STATUS.PENDING;
    const isFailed = currentStatus === REPORT_STATUS.FAILED;

    useEffect(() => {
        setCurrentStatus(status);
    }, [status]);

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    const handleRefetchStatus = async () => {
        if (!request_id) return;
        setLoading(true);
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActions.TAP_REFRESH_BTN,
                value: request_id,
                view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
            },
            { userDetails, isMobile }
        );
        try {
            const data = await refetchReportStatus(request_id);

            if (data?.status) {
                setSnackbar({
                    type: SNACKBAR_TYPES.SUCCESS,
                    message: SUCCESS_MESSAGES.GET_LATEST_STATUS,
                });
                setCurrentStatus(data?.status);
            } else {
                setSnackbar({
                    type: SNACKBAR_TYPES.FAILURE,
                    message:
                        data?.message ||
                        ERROR_MESSAGES.GET_LATEST_STATUS_FAILED,
                });
                sendSegmentEvent(
                    SegmentEventNameTypes.EXCEPTION,
                    {
                        message:
                            data?.message ||
                            ERROR_MESSAGES.GET_LATEST_STATUS_FAILED,
                        action_type: SegmentEventActionTypes.TAP_BTN,
                        action_name: SegmentEventActions.TAP_REFRESH_BTN,
                        view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                    },
                    { userDetails, isMobile }
                );
            }
        } catch (e: any) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message:
                    e?.response?.data?.message ||
                    ERROR_MESSAGES.GET_LATEST_STATUS_FAILED,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message:
                        e?.response?.data?.message ||
                        ERROR_MESSAGES.GET_LATEST_STATUS_FAILED,
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActions.TAP_REFRESH_BTN,
                    view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                },
                { userDetails, isMobile }
            );
            console.log('Error while refetching status', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Styles.ReportHistoryItem isLoading={loading}>
                <Styles.ReportDetails>
                    <Styles.ReportHeader>
                        <Styles.ReportTitle>
                            {'Commission Report'}
                        </Styles.ReportTitle>
                        <Styles.ReportStatus
                            isComplete={isComplete}
                            isFailed={isFailed}
                        >
                            <Styles.StatusIcon>
                                <Image
                                    src={getImageBasePath(
                                        isComplete
                                            ? '/icons/greenTick.svg'
                                            : isFailed
                                            ? '/icons/cross.svg'
                                            : '/icons/clock-icon.svg'
                                    )}
                                    alt=""
                                    width={'14px'}
                                    height={'14px'}
                                />
                            </Styles.StatusIcon>
                            {currentStatus}
                        </Styles.ReportStatus>
                    </Styles.ReportHeader>

                    <Styles.ReportData>{`Report duration: ${new Date(
                        from_date
                    ).toLocaleDateString('en-IN')} to ${new Date(
                        to_date
                    ).toLocaleDateString('en-IN')}`}</Styles.ReportData>
                    <Styles.ReportCreated>
                        {'Created on: '}
                        {new Date(created_on).toLocaleDateString('en-IN')}
                    </Styles.ReportCreated>
                    {(isPending || isProcessing) && (
                        <Styles.ReportInfoText>
                            {'Generating report.'}
                        </Styles.ReportInfoText>
                    )}
                    {isComplete && (
                        <Styles.ReportInfoText>
                            {'Note: The data is 1 hour behind real-time'}
                        </Styles.ReportInfoText>
                    )}
                </Styles.ReportDetails>
                <Styles.ReportActions>
                    {isComplete && (
                        <Styles.ActionButton
                            title="Download Report"
                            onClick={() => handleDownloadReport(request_id)}
                        >
                            <Image
                                src={getImageBasePath('/icons/download.svg')}
                                alt=""
                                width={'16px'}
                                height={'16px'}
                            />
                        </Styles.ActionButton>
                    )}
                    {(isProcessing || isPending) && (
                        <Styles.RetryButton
                            title="Get latest status"
                            onClick={() => handleRefetchStatus()}
                        >
                            <Image
                                src={getImageBasePath(
                                    '/icons/refresh-icon.svg'
                                )}
                                alt=""
                                width={'14px'}
                                height={'14px'}
                            />
                            {'Refresh Status'}
                        </Styles.RetryButton>
                    )}
                </Styles.ReportActions>
            </Styles.ReportHistoryItem>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default ReportCard;
